import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } else {
      toast.error(res.error || "Unable to sign in");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFD]">
      {/* Left - Form */}
      <div className="flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-[#0055FF] text-white flex items-center justify-center shadow-sm">
              <Wrench size={18} strokeWidth={2.4} />
            </div>
            <div>
              <p className="font-display font-bold text-xl leading-none tracking-tight">FixBoard</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">Maintenance OS</p>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            Sign in
          </h1>
          <p className="text-sm text-gray-600 mb-8">
            Report issues from your location or manage work across all 13 sites.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                autoComplete="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase text-gray-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="current-password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-medium"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  Sign in <ArrowRight size={16} className="ml-1" />
                </>
              )}
            </Button>

            <p className="text-sm text-gray-600 text-center">
              Don't have an account?{" "}
              <Link to="/signup" data-testid="link-to-signup" className="text-[#0055FF] font-medium hover:underline">
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right - Visual */}
      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%), url('https://images.unsplash.com/photo-1754666095009-5b81f7435a84?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute bottom-0 left-0 right-0 p-10 text-white">
          <p className="text-xs tracking-[0.2em] uppercase opacity-80 mb-3">Field-ready</p>
          <h2 className="font-display text-3xl font-bold tracking-tight leading-tight max-w-sm">
            One checklist. Thirteen locations. Zero missed tickets.
          </h2>
        </div>
      </div>
    </div>
  );
}
